import { useState } from "react";
import { useScrollAnimation } from "../../../hooks/useScrollAnimation";

interface CTASectionProps {
  onNavigate: (id: string) => void;
}

export default function CTASection({ onNavigate }: CTASectionProps) {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();
  const [showContact, setShowContact] = useState(false);
  const [contactData, setContactData] = useState({ name: "", email: "", message: "" });
  const [contactStatus, setContactStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (contactData.message.length > 500) return;
    setContactStatus("submitting");
    try {
      const body = new URLSearchParams();
      Object.entries(contactData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch("https://readdy.ai/api/form/d6utjilt4s7j0tv5i9eg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        setContactStatus("success");
        setContactData({ name: "", email: "", message: "" });
      } else {
        setContactStatus("error");
      }
    } catch {
      setContactStatus("error");
    }
  };

  return (
    <section className="relative py-20 md:py-40 overflow-hidden bg-[#3D2817]">
      {/* Radial glow */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_#6B4423_0%,_#3D2817_70%)]" />

      <div
        ref={ref}
        className={`relative z-10 max-w-[700px] mx-auto px-5 sm:px-8 md:px-10 text-center transition-all duration-1000 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}
      >
        <p
          className="text-[#D4AF87] text-[11px] uppercase tracking-[4px] mb-5 sm:mb-6"
          style={{ fontFamily: "Inter, sans-serif" }}
        >
          Begin Your Journey
        </p>
        <h2
          className="text-[#FAF8F3] text-4xl sm:text-5xl md:text-6xl leading-tight mb-6 sm:mb-8"
          style={{ fontFamily: "Cinzel, serif", fontWeight: 400 }}
        >
          Discover Rustic Copper
        </h2>
        <p
          className="text-[#FAF8F3]/60 text-base sm:text-lg leading-relaxed mb-10 sm:mb-14"
          style={{ fontFamily: "Inter, sans-serif", fontWeight: 300 }}
        >
          Each piece begins with a conversation. Let yours start today.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
          <button
            onClick={() => onNavigate("collection")}
            className="bg-[#D4AF87] text-[#3D2817] text-xs uppercase tracking-[2px] px-10 py-4 hover:bg-[#C9A07A] transition-colors duration-300 cursor-pointer whitespace-nowrap w-full sm:w-auto"
            style={{ fontFamily: "Inter, sans-serif", borderRadius: "2px" }}
          >
            Explore Collection
          </button>
          <button
            onClick={() => setShowContact(true)}
            className="border border-[#D4AF87]/60 text-[#D4AF87] text-xs uppercase tracking-[2px] px-10 py-4 hover:border-[#D4AF87] hover:bg-white/5 transition-all duration-300 cursor-pointer whitespace-nowrap w-full sm:w-auto"
            style={{ fontFamily: "Inter, sans-serif", borderRadius: "2px" }}
          >
            Contact Us
          </button>
        </div>
      </div>

      {/* Contact Modal */}
      {showContact && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4 py-6"
          onClick={(e) => { if (e.target === e.currentTarget) setShowContact(false); }}
        >
          <div className="bg-[#FAF8F3] max-w-lg w-full p-8 sm:p-12 relative rounded-lg max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setShowContact(false)}
              className="absolute top-6 right-6 w-8 h-8 flex items-center justify-center text-[#7A6F5D] hover:text-[#6B4423] cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-xl" />
            </button>
            <h3
              className="text-[#6B4423] text-2xl mb-2"
              style={{ fontFamily: "Cinzel, serif", fontWeight: 400 }}
            >
              Contact Us
            </h3>
            <p className="text-[#7A6F5D] text-sm mb-8" style={{ fontFamily: "Inter, sans-serif" }}>
              We&apos;d love to hear from you.
            </p>
            {contactStatus === "success" ? (
              <div className="text-center py-8">
                <p className="text-[#6B4423] text-lg mb-2" style={{ fontFamily: "Playfair Display, serif" }}>
                  Message Received
                </p>
                <p className="text-[#7A6F5D] text-sm" style={{ fontFamily: "Inter, sans-serif" }}>
                  We&apos;ll get back to you shortly.
                </p>
              </div>
            ) : (
              <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit} className="space-y-5">
                <div>
                  <label className="block text-[#6B4423] text-xs uppercase tracking-[2px] mb-2" style={{ fontFamily: "Inter, sans-serif" }}>Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={contactData.name}
                    onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                    className="w-full border-b border-[#7A6F5D]/30 bg-transparent py-3 text-sm text-[#3D2817] focus:outline-none focus:border-[#B76E79] transition-colors"
                    style={{ fontFamily: "Inter, sans-serif" }}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-[#6B4423] text-xs uppercase tracking-[2px] mb-2" style={{ fontFamily: "Inter, sans-serif" }}>Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={contactData.email}
                    onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                    className="w-full border-b border-[#7A6F5D]/30 bg-transparent py-3 text-sm text-[#3D2817] focus:outline-none focus:border-[#B76E79] transition-colors"
                    style={{ fontFamily: "Inter, sans-serif" }}
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-[#6B4423] text-xs uppercase tracking-[2px] mb-2" style={{ fontFamily: "Inter, sans-serif" }}>
                    Message ({contactData.message.length}/500)
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    value={contactData.message}
                    onChange={(e) => setContactData({ ...contactData, message: e.target.value })}
                    rows={3}
                    className="w-full border-b border-[#7A6F5D]/30 bg-transparent py-3 text-sm text-[#3D2817] focus:outline-none focus:border-[#B76E79] transition-colors resize-none"
                    style={{ fontFamily: "Inter, sans-serif" }}
                    placeholder="How can we help?"
                  />
                </div>
                <button
                  type="submit"
                  disabled={contactStatus === "submitting" || contactData.message.length > 500}
                  className="w-full py-4 bg-[#6B4423] text-[#D4AF87] text-xs uppercase tracking-[2px] hover:bg-[#8B5A3C] transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap mt-2"
                  style={{ fontFamily: "Inter, sans-serif", borderRadius: "2px" }}
                >
                  {contactStatus === "submitting" ? "Sending..." : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
